"use client";

import { useCallback, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { ChevronLeft } from "lucide-react";
import { LinkButton, toLinkArrowVariant } from "@/shared/components";
import { HamburgerMenu } from "@/shared/components/HamburgerMenu";
import type { SafeNavigationItem, SocialLinkType } from "@/lib/directus/typeGuards";
import type { ButtonDataType } from "@/shared/types";
import { ImageDataType } from "@/shared/types";
import { DesktopNav, type MenuPromo } from "./DesktopNav";
import { HeaderSearchToggle } from "./Search/HeaderSearchToggle";
import { CONTENT_SIDE_LINES, CONTENT_WIDTH } from "@/shared/components/BlockWrapper";
import { cn } from "@/lib/utils";

interface HeaderMainBarProps {
  logo?: ImageDataType | null;
  navigation?: SafeNavigationItem[] | null;
  headerCta?: ButtonDataType[];
  phone?: string | null;
  phoneSecondary?: string | null;
  email?: string | null;
  address?: string | null;
  socialLinks?: SocialLinkType[] | null;
  menuPromo?: MenuPromo | null;
}

export function HeaderMainBar({
  logo,
  navigation,
  headerCta,
  phone,
  phoneSecondary,
  email,
  address,
  socialLinks,
  menuPromo,
}: HeaderMainBarProps) {
  const primaryCta = headerCta?.[0];
  const pathname = usePathname();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [prevPathname, setPrevPathname] = useState(pathname);
  const [menuCanGoBack, setMenuCanGoBack] = useState(false);
  const menuGoBackRef = useRef<() => void>(() => {});

  if (pathname !== prevPathname) {
    setPrevPathname(pathname);
    setSearchOpen(false);
  }

  const handleSearchOpenChange = useCallback((open: boolean) => {
    setSearchOpen(open);
  }, []);

  const handleMobileMenuOpenChange = useCallback((open: boolean) => {
    setMenuOpen(open);
    if (open) {
      setSearchOpen(false);
    }
  }, []);

  const handleDesktopMenuOpenChange = useCallback((open: boolean) => {
    if (open) {
      setSearchOpen(false);
    }
  }, []);

  const logoLink = (
    <Link
      href="/"
      prefetch={false}
      aria-label="На главную"
      className="flex shrink-0 items-center"
    >
      {logo ? (
        <div className="header-logo-mark relative h-4 w-16 desktop:h-6 desktop:w-24">
          <Image
            src={logo.src}
            alt={logo.alt || "ZNAKI"}
            fill
            priority
            unoptimized
            className="object-contain object-left"
          />
        </div>
      ) : (
        <span className="text-sm font-bold uppercase tracking-widest text-primary desktop:text-lg">
          ZNAKI
        </span>
      )}
    </Link>
  );

  return (
    <div className="relative z-50 bg-background">
      <div
        className={cn(
          "header-main-bar-inner",
          CONTENT_WIDTH,
          !menuOpen && CONTENT_SIDE_LINES,
          "flex items-center justify-between",
          "h-[var(--header-main-height)] px-4 md:px-6 desktop:gap-6 desktop:px-10"
        )}
      >
        <div className="flex min-w-0 shrink-0 items-center">
          {menuOpen && menuCanGoBack ? (
            <button
              type="button"
              onClick={() => menuGoBackRef.current()}
              className="flex size-8 items-center justify-center text-foreground transition-colors hover:text-primary desktop:hidden"
              aria-label="Назад"
            >
              <ChevronLeft className="size-8" strokeWidth={1.5} aria-hidden />
            </button>
          ) : (
            logoLink
          )}
        </div>

        <div className="hidden min-w-0 flex-1 items-center justify-center desktop:flex">
          {navigation && navigation.length > 0 && (
            <DesktopNav
              items={navigation}
              menuPromo={menuPromo}
              searchOpen={searchOpen}
              onMenuOpenChange={handleDesktopMenuOpenChange}
            />
          )}
        </div>

        <div className="flex items-center gap-4 desktop:ms-auto desktop:gap-10">
          <HeaderSearchToggle
            key={pathname}
            isOpen={searchOpen}
            onOpenChange={handleSearchOpenChange}
          />

          {primaryCta && (
            <LinkButton
              href={primaryCta.href || "#"}
              variant={toLinkArrowVariant(primaryCta.variant)}
              size={primaryCta.size}
              className="header-cta hidden desktop:inline-flex"
            >
              {primaryCta.children}
            </LinkButton>
          )}

          <HamburgerMenu
            navigation={navigation}
            primaryCta={primaryCta}
            phone={phone}
            phoneSecondary={phoneSecondary}
            email={email}
            address={address}
            socialLinks={socialLinks}
            searchOpen={searchOpen}
            onOpenChange={handleMobileMenuOpenChange}
            onNavLevelChange={({ canGoBack, goBack }) => {
              setMenuCanGoBack(canGoBack);
              menuGoBackRef.current = goBack;
            }}
          />
        </div>
      </div>
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 z-[45] h-px bg-border"
        aria-hidden
      />
    </div>
  );
}
